import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface Methodology {
  name: string;
  approach: string;
  bestFor: string;
  icon: string;
  color: string;
}

interface LifecyclePhase {
  name: string;
  tasks: string;
  output: string;
}

interface MgmtTool {
  name: string;
  purpose: string;
  icon: string;
}

interface GanttTask {
  name: string;
  start: number;
  duration: number;
  color: string;
}

interface ProjectMgmtTemplateProps {
  topic: string;
  subtitle: string;
  methodologies: Methodology[];
  lifecycle: LifecyclePhase[];
  tools: MgmtTool[];
  ganttTasks: GanttTask[];
  riskLevels: { label: string; icon: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: ProjectMgmtTemplateProps = {
  topic: '项目管理方法论',
  subtitle: 'Project Management Methodology',
  methodologies: [
    {
      name: '瀑布模型',
      approach: '线性顺序',
      bestFor: '需求明确、变更少的项目',
      icon: '🌊',
      color: '#3b82f6',
    },
    {
      name: '敏捷开发',
      approach: '迭代增量',
      bestFor: '需求变化快、创新型项目',
      icon: '🔄',
      color: '#60a5fa',
    },
    {
      name: 'Scrum框架',
      approach: '冲刺迭代',
      bestFor: '团队协作、产品开发',
      icon: '🏃',
      color: '#93c5fd',
    },
    {
      name: '看板方法',
      approach: '持续流动',
      bestFor: '运维支持、持续交付',
      icon: '📋',
      color: '#bfdbfe',
    },
  ],
  lifecycle: [
    { name: '启动', tasks: '立项审批、干系人识别', output: '项目章程' },
    { name: '规划', tasks: 'WBS分解、进度计划', output: '项目管理计划' },
    { name: '执行', tasks: '团队管理、质量保证', output: '可交付成果' },
    { name: '监控', tasks: '进度跟踪、变更控制', output: '绩效报告' },
    { name: '收尾', tasks: '验收移交、经验总结', output: '项目总结报告' },
  ],
  tools: [
    { name: '甘特图', purpose: '可视化项目进度与任务依赖', icon: '📊' },
    { name: '燃尽图', purpose: '跟踪冲刺剩余工作量', icon: '📉' },
    { name: 'WBS分解', purpose: '层层拆解工作范围', icon: '🌳' },
    { name: '风险矩阵', purpose: '评估概率×影响等级', icon: '⚠️' },
  ],
  ganttTasks: [
    { name: '需求分析', start: 0, duration: 3, color: '#3b82f6' },
    { name: '系统设计', start: 2, duration: 4, color: '#60a5fa' },
    { name: '开发编码', start: 5, duration: 6, color: '#2563eb' },
    { name: '测试验证', start: 9, duration: 3, color: '#93c5fd' },
    { name: '部署上线', start: 11, duration: 2, color: '#1d4ed8' },
  ],
  riskLevels: [
    { label: '范围蔓延', icon: '🔍' },
    { label: '资源不足', icon: '👥' },
    { label: '技术风险', icon: '⚙️' },
    { label: '沟通障碍', icon: '📡' },
  ],
  stats: [
    { value: '70%', label: '项目失败因沟通不畅' },
    { value: '2-4周', label: 'Scrum冲刺周期' },
    { value: '37%', label: '需求变更是首要风险' },
  ],
  bottomQuote:
    '管理的本质不是控制一切，而是在不确定中建立秩序，在复杂中找到路径 —— 彼得·德鲁克',
};

export const ProjectMgmtTemplate: React.FC<
  Partial<ProjectMgmtTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const methodStart = 30;
  const lifecycleStart = 150;
  const ganttStart = 200;
  const toolStart = 300;
  const riskStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const ganttDraw = interpolate(
    frame,
    [ganttStart, ganttStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const maxWeek = 13;
  const ganttBarX = (week: number) => 150 + (week / maxWeek) * 540;
  const ganttBarW = (dur: number) => (dur / maxWeek) * 540;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0f1d36 40%, #070e1a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating task node particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 293) % 1920;
        const y = (i * 181) % 1080;
        const size = 3 + (i % 5);
        const drift = Math.sin(frame * 0.02 + i) * 8;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.045 + i * 0.6);
        const colors = ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb'];
        const isNode = i % 5 === 0;
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: isNode ? size * 1.8 : size,
              height: isNode ? size * 1.2 : size,
              borderRadius: isNode ? 3 : '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#1e40af',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#bfdbfe',
            flexShrink: 0,
          }}
        >
          📐 项目管理
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#dbeafe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Methodologies ===== */}
      <div style={{ position: 'absolute', top: 140, left: 36, width: 520 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          管理方法论
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.methodologies.map((m, i) => {
            const s = spring({
              frame: frame - (methodStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#0f172a',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${m.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${m.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${m.color}30`,
                  }}
                >
                  {m.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: m.color }}
                    >
                      {m.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#64748b' }}>
                      {m.approach}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}
                  >
                    {m.bestFor}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Project Lifecycle Flow */}
        <div style={{ marginTop: 30 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: -5,
              letterSpacing: '0.08em',
            }}
          >
            项目生命周期
          </div>
          <svg width="510" height="210" viewBox="0 0 510 210">
            {p.lifecycle.map((phase, i) => {
              const s = spring({
                frame: frame - (lifecycleStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const cx = 55 + i * 100;
              return (
                <g key={i} opacity={s}>
                  <circle
                    cx={cx}
                    cy="55"
                    r="34"
                    fill="#1e40af30"
                    stroke="#3b82f6"
                    strokeWidth="2"
                  />
                  <text
                    x={cx}
                    y="60"
                    fill="#bfdbfe"
                    fontSize="22"
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    {phase.name}
                  </text>
                  {i < p.lifecycle.length - 1 && (
                    <line
                      x1={cx + 36}
                      y1="55"
                      x2={cx + 64}
                      y2="55"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      markerEnd="url(#arrowBlue)"
                    />
                  )}
                  <text
                    x={cx}
                    y="112"
                    fill="#94a3b8"
                    fontSize="16"
                    textAnchor="middle"
                  >
                    {phase.tasks.split('、')[0]}
                  </text>
                  <text
                    x={cx}
                    y="132"
                    fill="#64748b"
                    fontSize="15"
                    textAnchor="middle"
                  >
                    → {phase.output}
                  </text>
                </g>
              );
            })}
            <defs>
              <marker
                id="arrowBlue"
                markerWidth="8"
                markerHeight="6"
                refX="8"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 8 3, 0 6" fill="#3b82f6" />
              </marker>
            </defs>
          </svg>
        </div>
      </div>

      {/* ===== CENTER: Gantt Chart ===== */}
      <div style={{ position: 'absolute', top: 140, left: 580, width: 800 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 20,
            letterSpacing: '0.08em',
          }}
        >
          甘特图 · 项目时间线
        </div>
        <svg width="720" height="370" viewBox="0 0 720 370">
          {/* Grid lines */}
          {Array.from({ length: 14 }).map((_, w) => (
            <g key={`grid-${w}`}>
              <line
                x1={ganttBarX(w)}
                y1="28"
                x2={ganttBarX(w)}
                y2="320"
                stroke="#1e3a5f"
                strokeWidth="1"
                opacity="0.2"
              />
              <text
                x={ganttBarX(w)}
                y="20"
                fill="#64748b"
                fontSize="20"
                textAnchor="middle"
              >
                W{w}
              </text>
            </g>
          ))}

          {p.ganttTasks.map((task, i) => {
            const s = spring({
              frame: frame - (ganttStart + 20 + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const yPos = 42 + i * 55;
            return (
              <g key={i} opacity={s}>
                <text
                  x="10"
                  y={yPos + 24}
                  fill="#bfdbfe"
                  fontSize="22"
                  fontWeight="600"
                >
                  {task.name}
                </text>
                <rect
                  x={ganttBarX(task.start)}
                  y={yPos + 4}
                  width={ganttBarW(task.duration) * ganttDraw}
                  height="32"
                  rx="8"
                  fill={task.color}
                  opacity={0.8}
                />
                <text
                  x={ganttBarX(task.start) + 8}
                  y={yPos + 27}
                  fill="#0f172a"
                  fontSize="20"
                  fontWeight="700"
                >
                  {task.duration}周
                </text>
              </g>
            );
          })}

          {/* Critical path indicator */}
          <g
            opacity={interpolate(
              frame,
              [ganttStart + 80, ganttStart + 100],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <line
              x1={ganttBarX(0)}
              y1="330"
              x2={ganttBarX(13)}
              y2="330"
              stroke="#f87171"
              strokeWidth="2.5"
              strokeDasharray="8 4"
            />
            <text x={ganttBarX(4)} y="360" fill="#f87171" fontSize="22" fontWeight="700">
              关键路径 · Critical Path
            </text>
          </g>
        </svg>

        {/* Management Tools */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            核心工具
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {p.tools.map((t, i) => {
              const s = spring({
                frame: frame - (toolStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#0f172a',
                    borderRadius: 12,
                    padding: '10px 12px',
                    border: '1.5px solid #1e3a5f',
                    opacity: s,
                    transform: `translateX(${(1 - s) * 24}px)`,
                  }}
                >
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{t.icon}</span>
                  <div>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#60a5fa' }}>
                      {t.name}
                    </span>
                    <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                      {t.purpose}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Risk Management ===== */}
      <div style={{ position: 'absolute', top: 140, right: 36, width: 480 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          风险管理
        </div>
        {/* Risk Matrix SVG */}
        <svg width="460" height="320" viewBox="0 0 460 320">
          <text x="10" y="24" fill="#94a3b8" fontSize="24">
            影响程度 ↑
          </text>
          <text x="350" y="310" fill="#94a3b8" fontSize="24">
            发生概率 →
          </text>
          {/* Matrix cells */}
          {[
            { x: 70, y: 35, c: '#22c55e30', t: '低' },
            { x: 190, y: 35, c: '#eab30830', t: '中' },
            { x: 310, y: 35, c: '#ef444430', t: '高' },
            { x: 70, y: 120, c: '#22c55e30', t: '低' },
            { x: 190, y: 120, c: '#eab30830', t: '中' },
            { x: 310, y: 120, c: '#ef444430', t: '高' },
            { x: 70, y: 205, c: '#eab30830', t: '中' },
            { x: 190, y: 205, c: '#ef444430', t: '高' },
            { x: 310, y: 205, c: '#dc262630', t: '极高' },
          ].map((cell, i) => {
            const s = spring({
              frame: frame - (riskStart + i * 6),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <g key={i} opacity={s}>
                <rect
                  x={cell.x}
                  y={cell.y}
                  width="110"
                  height="75"
                  rx="8"
                  fill={cell.c}
                  stroke="#1e3a5f"
                  strokeWidth="1"
                />
                <text
                  x={cell.x + 55}
                  y={cell.y + 44}
                  fill="#94a3b8"
                  fontSize="22"
                  fontWeight="600"
                  textAnchor="middle"
                >
                  {cell.t}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Risk Categories */}
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            常见风险类型
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {p.riskLevels.map((risk, i) => {
              const s = spring({
                frame: frame - (riskStart + 60 + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0f172a',
                    borderRadius: 14,
                    padding: '16px 12px',
                    textAlign: 'center',
                    border: '1px solid #1e3a5f',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 6 }}>{risk.icon}</div>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#93c5fd' }}
                  >
                    {risk.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f172a',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e3a5f',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 30, fontWeight: 900, color: '#3b82f6' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0f172a',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #1e40af',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>📐</span>
          <span style={{ fontSize: 22, color: '#dbeafe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #1e40af, #3b82f6, #93c5fd)',
        }}
      />
    </AbsoluteFill>
  );
};
